/**
 * Remember how far down an article the member has read, and put them back there.
 *
 * The fraction is measured the way `useScrollDepth` measures it — how much of the body
 * has passed the *bottom* of the viewport — so the number the server stores means the
 * same thing as the analytics milestones. It is a high-water mark on this side too:
 * scrolling back up to the back link before leaving is not "unread", and the save on the
 * way out must carry the furthest point, not the last one. Saves are throttled: a scroll
 * fires far faster than a position is worth writing, and the mark goes out when the page
 * is left (unmount, or the tab being hidden, which is what a phone does on every switch).
 *
 * Restoring is opt-in per visit (`restore`), because jumping a reader down the page on
 * every open would feel like a bug on the second read; the "Pokračovat" card asks for
 * it, a tap from the feed does not.
 */

import { type RefObject, useEffect, useRef } from "react";

/** How often a changed position may be written. */
const SAVE_INTERVAL_MS = 3_000;
/** Below this difference a position is "the same place" and is not re-saved. */
const MIN_DELTA = 0.02;
/** Below this the member has not started; nothing is saved or restored. */
const MIN_FRACTION = 0.03;

export interface ReadingPositionOptions {
  /** Identifies the article; null while nothing is loaded. */
  key: string | null;
  /** The stored fraction, or null when the member never started. */
  initialFraction: number | null;
  /** Whether to scroll back to `initialFraction` once the body has laid out. */
  restore: boolean;
  onSave: (fraction: number) => void;
}

export function readingFraction(body: HTMLElement): number | null {
  const rect = body.getBoundingClientRect();
  if (rect.height <= 0) return null;
  const seen = window.innerHeight - rect.top;
  return Math.min(1, Math.max(0, seen / rect.height));
}

export function useReadingPosition(
  bodyRef: RefObject<HTMLElement | null>,
  { key, initialFraction, restore, onSave }: ReadingPositionOptions,
): void {
  const latestSave = useRef(onSave);
  useEffect(() => {
    latestSave.current = onSave;
  });

  useEffect(() => {
    const body = bodyRef.current;
    if (!body || key === null) return;

    let lastSaved = initialFraction ?? 0;
    let pending: number | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let frame = 0;

    const flush = () => {
      timer = undefined;
      if (pending === null) return;
      const fraction = pending;
      pending = null;
      if (fraction < MIN_FRACTION || Math.abs(fraction - lastSaved) < MIN_DELTA)
        return;
      lastSaved = fraction;
      latestSave.current(fraction);
    };

    const measure = () => {
      frame = 0;
      const fraction = readingFraction(body);
      if (fraction === null) return;
      pending = Math.max(pending ?? 0, fraction);
      if (timer === undefined) timer = setTimeout(flush, SAVE_INTERVAL_MS);
    };

    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(measure);
    };

    const flushNow = () => {
      if (timer !== undefined) clearTimeout(timer);
      // Measure once more so the very last scroll counts; a detached body (React has
      // already removed it by the time this cleanup runs) measures as nothing.
      const fraction = readingFraction(body);
      if (fraction !== null) pending = Math.max(pending ?? 0, fraction);
      flush();
    };

    const onHide = () => {
      if (document.visibilityState === "hidden") flushNow();
    };

    if (
      restore &&
      initialFraction !== null &&
      initialFraction >= MIN_FRACTION
    ) {
      // Two frames: Markdown and the gallery need a layout pass before the body has
      // its final height, and scrolling against the wrong height lands too early.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const rect = body.getBoundingClientRect();
          const target =
            window.scrollY +
            rect.top +
            initialFraction * rect.height -
            window.innerHeight;
          window.scrollTo({ top: Math.max(0, target) });
        });
      });
    }

    window.addEventListener("scroll", schedule, { passive: true });
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flushNow);
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flushNow);
      flushNow();
    };
    // `initialFraction` is read once per article on purpose: it is the place to come
    // back to, not something to chase as the saves come back from the server.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyRef, key, restore]);
}
